import { ARENA } from '../../config/constants.js';

const CONTACT_COOLDOWN_DURATION = 0.5; // seconds between contact-damage ticks while overlapping the player
const HIT_FLASH_DURATION = 0.08; // brief white flinch flash on taking damage

/**
 * The baseline wave-defense enemy: chases the player, deals contact damage
 * on overlap (cooldown-gated so standing in it doesn't melt the player every
 * tick), and periodically fires a slow shot toward the player — the same
 * pooled-projectile shape as BossBullet (see MinionWaveSystem's BulletPool).
 * Hittable by the player's normal weapons via CombatSystem's enemy registry
 * (see CombatSystem.getNearestTarget/_checkCollisions). Siblings: Charger.js
 * (telegraph + straight-line dash), Bomber.js (explodes on death).
 */
export class Skirmisher {
  constructor(eventBus, {
    x, y,
    radius = 6,
    hp,
    moveSpeed,
    contactDamage,
    shotDamage,
    shotSpeed,
    fireInterval,
  }) {
    this.eventBus = eventBus;
    this.type = 'skirmisher';
    this.x = x;
    this.y = y;
    this.radius = radius;
    this.maxHp = hp;
    this.hp = hp;
    this.moveSpeed = moveSpeed;
    this.contactDamage = contactDamage;
    this.shotDamage = shotDamage;
    this.shotSpeed = shotSpeed;
    this._fireInterval = fireInterval;
    // Desyncs minions spawned in the same wave so they don't all fire in lockstep.
    this._fireTimer = fireInterval * Math.random();
    this._contactCooldown = 0;
    this.isDead = false;
    this.hitFlashTimer = 0;
  }

  update(dt, player, bulletPool) {
    this.hitFlashTimer = Math.max(0, this.hitFlashTimer - dt);

    const dx = player.x - this.x;
    const dy = player.y - this.y;
    const dist = Math.hypot(dx, dy) || 1;
    this.x += (dx / dist) * this.moveSpeed * dt;
    this.y += (dy / dist) * this.moveSpeed * dt;

    this._fireTimer -= dt;
    if (this._fireTimer <= 0) {
      this._fireTimer = this._fireInterval;
      const angle = Math.atan2(dy, dx);
      bulletPool.fire({
        x: this.x,
        y: this.y,
        vx: Math.cos(angle) * this.shotSpeed,
        vy: Math.sin(angle) * this.shotSpeed,
        radius: 2,
        damage: this.shotDamage,
        color: '#e8873a',
        lifetime: 4,
      });
    }

    this._clampToArena();

    this._contactCooldown = Math.max(0, this._contactCooldown - dt);
    if (this._contactCooldown <= 0 && dist < this.radius + player.radius) {
      player.takeDamage(this.contactDamage, this.type);
      this._contactCooldown = CONTACT_COOLDOWN_DURATION;
    }
  }

  // Defense-in-depth — no minion should ever be able to wander outside the
  // fightable area and become unreachable, softlocking the wave (see
  // MinionWaveSystem.isStageCleared / Charger's charge-duration cap).
  _clampToArena() {
    this.x = Math.min(ARENA.x + ARENA.width - this.radius, Math.max(ARENA.x + this.radius, this.x));
    this.y = Math.min(ARENA.y + ARENA.height - this.radius, Math.max(ARENA.y + this.radius, this.y));
  }

  /** Same shape as Boss.takeDamage — used by CombatSystem's shared enemy collision loop. */
  takeDamage(amount) {
    if (this.isDead) return;
    const hpBefore = this.hp;
    this.hp = Math.max(0, this.hp - amount);
    // Emit the clamped HP delta, not the raw incoming hit — see
    // Boss.takeDamage's identical fix, same overkill-XP issue here on a
    // one-shot kill.
    const appliedDamage = hpBefore - this.hp;
    this.hitFlashTimer = HIT_FLASH_DURATION;
    this.eventBus.emit('minion:damaged', { amount: appliedDamage, x: this.x, y: this.y, minion: this });
    if (this.hp <= 0) {
      this.isDead = true;
      this.eventBus.emit('minion:died', { x: this.x, y: this.y, minion: this });
    }
  }
}
